import { useEffect, useRef } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { AiPrintoutData } from "../../../downloads/aiPrintout/contract";
import type { CustomPrintoutGenerationOutcome } from "../../../hooks/useCustomPrintoutGeneration";
import type { RepairRequest } from "./customPrintoutDraftState";
import { type CustomPrintoutGenerationRequest, repairRequestOf } from "./customPrintoutRequests";

type Params = {
	/** Set by the reducer when a fresh draft needs its one repair. */
	repairRequest: RepairRequest | null;
	printoutId: string | null;
	data: AiPrintoutData | null;
	runGeneration: (request: CustomPrintoutGenerationRequest) => Promise<CustomPrintoutGenerationOutcome>;
	/** Records the repair's outcome in the draft, as a request with no words of its own, at the repair's level. */
	onSettled: (outcome: CustomPrintoutGenerationOutcome, request: RepairRequest) => void;
};

/** Starts the single automatic repair the reducer asked for — once per request, however often this re-renders. */
export const useAutoRepair = ({ repairRequest, printoutId, data, runGeneration, onSettled }: Params): void => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const startedRef = useRef<RepairRequest | null>(null);
	useEffect(() => {
		if (repairRequest === null || startedRef.current === repairRequest || data === null) return;
		startedRef.current = repairRequest;
		track("custom_printout_auto_repair", { department_id: departmentId, failure_kind: repairRequest.repair.kind });
		void runGeneration(repairRequestOf(printoutId, data, repairRequest)).then((outcome) => onSettled(outcome, repairRequest));
	}, [repairRequest, data, track, departmentId, runGeneration, printoutId, onSettled]);
};
